import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

interface TypingAnimationProps {
  texts: string[];
  speed?: number;
  deleteSpeed?: number;
  pauseDuration?: number;
  className?: string;
  infinite?: boolean;
}

const TypingContainer = styled.span`
  position: relative;
  display: inline-block;
`;

const Cursor = styled.span<{ $isVisible: boolean }>`
  display: inline-block;
  width: 3px;
  height: 1.2em;
  background-color: currentColor;
  margin-left: 4px;
  opacity: ${props => props.$isVisible ? 1 : 0};
  transition: opacity 0.2s ease;
  animation: ${props => props.$isVisible ? 'blink 1.2s infinite' : 'none'};
  border-radius: 1px;
  box-shadow: 0 0 8px currentColor;
  
  @keyframes blink {
    0%, 45% { 
      opacity: 1;
      transform: scaleY(1);
    }
    50%, 95% { 
      opacity: 0;
      transform: scaleY(0.8);
    }
    100% { 
      opacity: 1;
      transform: scaleY(1);
    }
  }
`;

const TypingAnimation: React.FC<TypingAnimationProps> = ({
  texts,
  speed = 100,
  deleteSpeed = 50,
  pauseDuration = 2000,
  className,
  infinite = true
}) => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (isComplete && !infinite) return;

    const currentFullText = texts[currentTextIndex];
    
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        // Typing
        if (currentText.length < currentFullText.length) {
          setCurrentText(currentFullText.slice(0, currentText.length + 1));
        } else {
          // Finished typing current text
          if (infinite || currentTextIndex < texts.length - 1) {
            setTimeout(() => setIsDeleting(true), pauseDuration);
          } else {
            setIsComplete(true);
          }
        }
      } else {
        // Deleting
        if (currentText.length > 0) {
          setCurrentText(currentText.slice(0, -1));
        } else {
          // Finished deleting, move to next text
          setIsDeleting(false);
          if (infinite) {
            setCurrentTextIndex((prevIndex) => (prevIndex + 1) % texts.length);
          } else if (currentTextIndex < texts.length - 1) {
            setCurrentTextIndex(currentTextIndex + 1);
          }
        }
      }
    }, isDeleting ? deleteSpeed : speed);

    return () => clearTimeout(timeout);
  }, [currentText, currentTextIndex, isDeleting, texts, speed, deleteSpeed, pauseDuration, infinite, isComplete]);

  return (
    <TypingContainer className={className}>
      {currentText}
      <Cursor $isVisible={!isComplete || infinite} />
    </TypingContainer>
  );
};

export default TypingAnimation;